<template>
	<div ref="modalEl" class="modal fade" tabindex="-1" aria-hidden="true">
		<div class="modal-dialog modal-dialog-centered modal-sm">
			<div class="modal-content cm-content">
				<div class="modal-header cm-header">
					<h6 class="modal-title cm-title">
						<i class="fas me-2" :class="[iconClass, danger ? 'cm-title-icon--danger' : 'cm-title-icon']"></i>
						{{ title }}
					</h6>
				</div>
				<div class="modal-body cm-body">
					<slot>{{ message }}</slot>
				</div>
				<div class="modal-footer cm-footer">
					<button type="button" class="cm-btn" @click="close">{{ cancelText }}</button>
					<button
						type="button"
						class="cm-btn"
						:class="danger ? 'cm-btn--danger' : 'cm-btn--primary'"
						:disabled="busy"
						@click="$emit('confirm')"
					>
						<i v-if="busy" class="fas fa-spinner fa-spin"></i>
						<i v-else-if="confirmIcon" class="fas" :class="confirmIcon"></i>
						{{ confirmText }}
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'

// Reusable confirmation modal. Parent controls it via refs:
//   this.$refs.myModal.open()  /  .close()
// and reacts to the `confirm` event (parent does the async work, sets
// :busy, then calls .close()).
export default {
	name: 'ConfirmModal',
	props: {
		title: { type: String, default: 'Are you sure?' },
		message: { type: String, default: '' },
		confirmText: { type: String, default: 'Confirm' },
		cancelText: { type: String, default: 'Cancel' },
		confirmIcon: { type: String, default: '' },
		iconClass: { type: String, default: 'fa-triangle-exclamation' },
		danger: { type: Boolean, default: false },
		busy: { type: Boolean, default: false },
	},
	data() {
		return { modal: null }
	},
	mounted() {
		this.modal = new Modal(this.$refs.modalEl)
	},
	beforeDestroy() {
		if (this.modal) this.modal.dispose()
	},
	methods: {
		open() {
			if (this.modal) this.modal.show()
		},
		close() {
			if (this.modal) this.modal.hide()
			this.$emit('cancel')
		},
	},
}
</script>

<style scoped lang="scss">
$cm-border: #e6e8ec;
$cm-text: #2b303b;
$cm-hover: #f3f5f9;

.cm-content {
	border: 1px solid $cm-border;
	border-radius: 14px;
}

/* No divider lines on header/footer. */
.cm-header,
.cm-footer {
	border: none;
}

.cm-header {
	padding-bottom: 0.25rem;
}

.cm-title {
	display: flex;
	align-items: center;
	font-size: 0.92rem;
	font-weight: 700;
	color: $cm-text;
	margin: 0;
}

.cm-title-icon {
	color: #8a909c;
}

.cm-title-icon--danger {
	color: #dc3545;
}

.cm-body {
	font-size: 0.82rem;
	color: #4a5260;
	padding-top: 0.25rem;
	padding-bottom: 0.5rem;
}

.cm-footer {
	padding-top: 0.25rem;
	gap: 0.25rem;
}

/* Compact neutral button. */
.cm-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	padding: 0.28rem 0.7rem;
	background: transparent;
	border: 1px solid $cm-border;
	border-radius: 8px;
	color: $cm-text;
	font-size: 0.82rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

	&:hover:not(:disabled) {
		background: $cm-hover;
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

.cm-btn--danger {
	color: #dc3545;
	border-color: #f1c2c7;

	&:hover:not(:disabled) {
		background: #fdecea;
		border-color: #eba1a8;
		color: #dc3545;
	}
}

.cm-btn--primary {
	color: #2b6cff;
	border-color: #b6cfff;

	&:hover:not(:disabled) {
		background: #ebf2ff;
		border-color: #8fb4ff;
		color: #2b6cff;
	}
}
</style>
